import React, { useEffect, useMemo, useState } from 'react'
import { useI18n } from '@ggs/i18n'
import { get, first, join, isArray } from 'lodash'
import { Divider, Link, Typography } from '@mui/material'
import { useAcademicApi, useAcademicDataDigest, useAcademicExpApi } from '../../../local'
import { Loader } from '../../loader'
import { StackTable } from '../../tables'
import { useSelector } from '@ggs/store'
import './CourseSectionView.scss'

/**
 * @typedef {import('../../../definitions').CourseSectionExpRecord} CourseSectionExpRecord
*/

/**
 * @typedef {Object} CourseViewProps
 */

/**
 * Specific course section detailed view.
 * @return {JSX.Element}
 */
const CourseSectionView = () => {
  const { t } = useI18n()
  const { getCourseSection } = useAcademicExpApi()
  const { getCourseSections } = useAcademicApi()
  const {
    composeSectionDetailTableData,
    composeSectionScheduleTableData,
    getCourseSectionInstructors,
  } = useAcademicDataDigest()
  const academicPeriod = useSelector((state) => state.settings.academicPeriod)
  const campus = useSelector((state) => state.settings.campus)
  const [errorMessage, setErrorMessage] = useState(null)
  const pageSize = 500

  const courseSectionId = get(
    window,
    'drupalSettings.react.courseSectionId',
    'COURSE_SECTION_DEFINITION-6-290401'
  )

  const [clusterDescriptions, setClusterDescriptions] = useState('')

  /**
   * @type {CourseSectionExpRecord}
   */
  const defaultCourseSection = null
  const [courseSection, setCourseSection] = useState(defaultCourseSection)
  const [loading, setLoading] = useState(true)

  const resolveCourseSectionData = async () => {
    const courseSectionRecord = await getCourseSection(courseSectionId)
    if (courseSectionRecord) {
      setCourseSection(courseSectionRecord)
      setLoading(false)
    }

    const clusters = get(courseSectionRecord, 'clusteredCourseSectionIds', null)
    if (clusters) {
      const clusterCourseSectionsResult = await getCourseSections({
        pageSize,
        courseSectionId: clusters,
      })

      if (isArray(clusterCourseSectionsResult.pageItems)) {
        const clusterSectionsRecords = clusterCourseSectionsResult.pageItems
        const clusterDescriptions = clusterSectionsRecords.map(
          (courseSection) => {
            const sectionListing = first(courseSection.sectionListings)
            return sectionListing?.sectionListingName || t('')
          }
        )

        setClusterDescriptions(join(clusterDescriptions, '<br/>'))
      }
    }
  }

  useEffect(() => {
    resolveCourseSectionData().then(() =>
      console.log('Fetched course section record from Academic Exp API.')
    )
  }, [])

  const sectionData = useMemo(() => {
    if (courseSection) {
      return {
        details: composeSectionDetailTableData(courseSection),
        schedules: composeSectionScheduleTableData(courseSection),
        instructors: getCourseSectionInstructors(courseSection),
      }
    }

    return null
  }, [courseSection])

  /**
   * Set visibility of node elements.
   * @param {string} visibility
   */
  const nodeElementsDisplay = (visibility) => {
    const nodeTitle = document.querySelector('.l-node__title')
    const nodeBody = document.querySelector('.l-node__body')

    if (nodeTitle) {
      // @ts-ignore
      nodeTitle.style.display = visibility
    }

    if (nodeBody) {
      // @ts-ignore
      nodeBody.style.display = visibility
    }
  }

  /**
   * Redirects user to search courses page when selected academic period or campus changes.
   */
  const searchCourseOnSettingsChange = () => {
    if (!courseSection) {
      return
    }

    const sectionMatchAcademicPeriod =
      courseSection.academicPeriod?.academicPeriodId === academicPeriod
    if (sectionMatchAcademicPeriod) {
      nodeElementsDisplay('block')
      setErrorMessage(null)
    } else {
      nodeElementsDisplay('none')
      setErrorMessage(
        t('The requested section is no longer offered at selected campus / academic period.')
      )
    }
  }

  useEffect(searchCourseOnSettingsChange, [courseSection, academicPeriod, campus])

  if (loading) {
    return <Loader />
  }

  if (errorMessage) {
    return (
      <div className="course-section-view">
        <Typography>{errorMessage}</Typography>
      </div>
    )
  }

  const { details, schedules, instructors } = sectionData

  return (
    <div className="course-section-view">
      <div className="course-section-view__details">
        {details.map((detail) => (
          <div key={detail.field} className="course-section-view__detail">
            <h5 className="course-section-view__label">{detail.label}:</h5>
            <span>{detail.value || ''}</span>
          </div>
        ))}
      </div>
      <div className="course-section-view__withdraw">
        <h5 className="course-section-view__subtitle">{t('Withdrawal Details')}</h5>
        {campus.endsWith('V') && (
          <Link href="https://www.calendar.ubc.ca/vancouver/index.cfm?page=deadlines">
            https://www.calendar.ubc.ca/vancouver/index.cfm?page=deadlines
          </Link>
        )}
        {campus.endsWith('O') && (
          <Link href="https://www.calendar.ubc.ca/okanagan/index.cfm?go=deadlines">
            https://www.calendar.ubc.ca/okanagan/index.cfm?go=deadlines
          </Link>
        )}

      </div>
      <div className="course-section-view__schedule">
        {schedules.length > 0 &&
          schedules.map((schedule, scheduleId) => (
            <div key={scheduleId}>
              <StackTable
                tableFields={schedule}
                columnSizes={{ lg: 3, md: 2, sm: 1 }}
              />
              <Divider sx={{ my: 1 }} />
            </div>
          ))}
      </div>
      <div className="course-section-view__info">
        <div className="course-section-view__instructor">
          <h5 className="course-section-view__label">{t('Instructor(s)')}:</h5>
          <div className="course-section-view__info-content">
            {instructors && instructors.length > 0
              ? instructors.map((instructor) => {
                return (
                  <div
                    key={instructor.employeeId}
                  >
                    {instructor.fullName}
                  </div>
                )
              })
              : ''}
          </div>
        </div>
        <div className="course-section-view__clusters">
          <h5 className="course-section-view__label">{t('Cluster Section(s)')}:</h5>
          <div
            className="course-section-view__info-content"
            dangerouslySetInnerHTML={{ __html: clusterDescriptions || '' }}
          />
        </div>
      </div>
      <div className="course-section-view__notes">
        <div>{t('Notes')}:</div>
        <div dangerouslySetInnerHTML={{ __html: `${courseSection?.publicNotes || ''}` }} />
      </div>
    </div>
  )
}

export default CourseSectionView

//{{academic.url}}/courses?pageSize=50&useEffectiveDate=false&courseInstanceId=COURSE_DEFINITION-3-14634_19950901
